/**
 * Browser-side WebSocket client for an ESP32 button device.
 *
 * Wire protocol (ESP32 → browser, JSON text frames):
 *   { "t": "btn",   "id": "<deviceId>", "s": "down"|"up", "ms": <millis> }
 *   { "t": "hb",    "id": "<deviceId>", "ms": <millis>, "uptime": <sec> }
 *   { "t": "hello", "id": "<deviceId>", "fw": "<ver>", "btn": "down"|"up" }
 *
 * Browser → ESP32 (optional):
 *   { "t": "led", "rgb": [r, g, b] }
 *   { "t": "identify" }
 *
 * One instance per ESP32 URL.  Reconnects automatically with capped
 * exponential backoff.  Synthesises a synthetic "up" event whenever the
 * connection drops while a "down" is outstanding (stuck-hook guard).
 */

export type EspButtonEvent =
  | { t: 'btn';   id: string; s: 'down' | 'up'; ms: number }
  | { t: 'hb';    id: string; ms: number; uptime: number }
  | { t: 'hello'; id: string; fw: string; btn?: 'down' | 'up' };

export interface EspButtonHandlers {
  onDown?(deviceId: string): void;
  onUp?(deviceId: string): void;
  onPresence?(deviceId: string, online: boolean): void;
}

export class EspButtonClient {
  private ws: WebSocket | null = null;
  private closedByUser = false;
  private backoff = 250;
  private deviceId: string | null = null;
  private lastHeartbeatMs = 0;
  private presenceTimer: ReturnType<typeof setInterval> | null = null;
  private lastReportedState: 'down' | 'up' = 'up';

  constructor(
    private readonly url: string,
    private readonly handlers: EspButtonHandlers = {},
  ) {}

  connect(): void {
    this.closedByUser = false;
    this.open();
    this.presenceTimer ??= setInterval(() => this.checkPresence(), 2000);
  }

  close(): void {
    this.closedByUser = true;
    if (this.presenceTimer) { clearInterval(this.presenceTimer); this.presenceTimer = null; }
    this.flushDownIfNeeded();
    this.ws?.close();
    this.ws = null;
  }

  /** Send a command to the device (fire-and-forget; no-op if not connected). */
  send(payload: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(payload));
  }

  private open(): void {
    if (this.closedByUser) return;
    let ws: WebSocket;
    try { ws = new WebSocket(this.url); }
    catch { this.scheduleReconnect(); return; }
    this.ws = ws;

    ws.addEventListener('open', () => {
      this.backoff = 250;
      this.lastHeartbeatMs = performance.now();
    });

    ws.addEventListener('message', (ev) => {
      try { this.dispatch(JSON.parse(ev.data as string) as EspButtonEvent); }
      catch { /* malformed frame — ignore */ }
    });

    ws.addEventListener('close', () => {
      this.flushDownIfNeeded();
      if (this.deviceId) this.handlers.onPresence?.(this.deviceId, false);
      if (this.ws === ws) this.ws = null;
      this.scheduleReconnect();
    });

    ws.addEventListener('error', () => {
      // 'close' always follows 'error' — let the close handler drive reconnect.
    });
  }

  private dispatch(msg: EspButtonEvent): void {
    this.deviceId = msg.id;
    this.lastHeartbeatMs = performance.now();

    if (msg.t === 'hello') {
      this.handlers.onPresence?.(msg.id, true);
      // hello.btn carries current debounced state so a tab joining mid-press
      // immediately gets the right state (stuck-hook guard for late joins).
      const initial = msg.btn;
      if (initial === 'down' && this.lastReportedState !== 'down') {
        this.lastReportedState = 'down';
        this.handlers.onDown?.(msg.id);
      } else if (initial === 'up' && this.lastReportedState === 'down') {
        this.lastReportedState = 'up';
        this.handlers.onUp?.(msg.id);
      }
      return;
    }

    if (msg.t === 'btn') {
      if (msg.s === 'down' && this.lastReportedState !== 'down') {
        this.lastReportedState = 'down';
        this.handlers.onDown?.(msg.id);
      } else if (msg.s === 'up' && this.lastReportedState === 'down') {
        this.lastReportedState = 'up';
        this.handlers.onUp?.(msg.id);
      }
    }
    // 'hb' messages just refresh lastHeartbeatMs (already done above).
  }

  /** If a "down" was outstanding, synthesise an "up" so the hook releases. */
  private flushDownIfNeeded(): void {
    if (this.lastReportedState === 'down' && this.deviceId) {
      this.lastReportedState = 'up';
      this.handlers.onUp?.(this.deviceId);
    }
  }

  /** Fired every 2 s; trips offline presence + stuck-hook flush after 12 s silence. */
  private checkPresence(): void {
    if (!this.deviceId) return;
    if (performance.now() - this.lastHeartbeatMs > 12_000) {
      this.flushDownIfNeeded();
      this.handlers.onPresence?.(this.deviceId, false);
    }
  }

  private scheduleReconnect(): void {
    if (this.closedByUser) return;
    const delay = Math.min(this.backoff, 5000);
    this.backoff = Math.min(this.backoff * 2, 5000);
    setTimeout(() => this.open(), delay);
  }
}
